import { TrendingUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CostHistoryEntry } from '../../../types';

interface CostTrendDataPoint {
  date: string;
  cost: number;
  supplier: string;
  fullDate: string;
}

interface CostTrendChartProps {readonly costHistory: CostHistoryEntry[];}

export function CostTrendChart({ costHistory }: CostTrendChartProps) {
  const chartData: CostTrendDataPoint[] = [...costHistory]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((entry) => ({
      date: new Date(entry.date).toLocaleDateString('en-US', {
        month: 'short',
        year: '2-digit',
      }),
      cost: entry.unitCost,
      supplier: entry.supplierName,
      fullDate: entry.date,
    }));

  const costs = chartData.map((d) => d.cost);
  const minCost = Math.floor(Math.min(...costs) - 1);
  const maxCost = Math.ceil(Math.max(...costs) + 1);

  return (
    <div className="rounded-lg border p-4">
      <h3 className="flex items-center gap-2 font-medium text-gray-900 mb-4">
        <TrendingUp className="h-4 w-4" />
        Cost Trends
      </h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 10 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
            <YAxis
              domain={[minCost, maxCost]}
              tick={{ fontSize: 12 }}
              stroke="#9ca3af"
              tickFormatter={(v: number) => `$${v.toFixed(0)}`}
            />
            <Tooltip content={<CostTooltip />} />
            <Line
              type="monotone"
              dataKey="cost"
              stroke="#2563eb"
              strokeWidth={2}
              dot={{
                fill: '#2563eb',
                r: 4 
              }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface CostTooltipProps {
  readonly active?: boolean;
  readonly payload?: Array<{ payload: CostTrendDataPoint }>;
}

function CostTooltip({
  active, payload 
}: CostTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="rounded-lg border bg-white p-3 shadow-lg">
      <p className="text-sm font-medium">${data.cost.toFixed(2)}/unit</p>
      <p className="text-xs text-gray-500">{data.fullDate}</p>
      <p className="text-xs text-gray-500">{data.supplier}</p>
    </div>
  );
}
